/**
 * AuthContext - Privy-based authentication for OwnYou
 *
 * Sprint 13: Complete rewrite from mock wallet to Privy embedded wallet.
 * Provides wallet-derived encryption keys via @ownyou/identity.
 *
 * Consumers use useAuth() to get: isAuthenticated, wallet, keys, connect, disconnect
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { usePrivy, useWallets } from '@privy-io/react-auth';
import {
  PrivyAdapter,
  KeyDerivation,
  type Wallet,
  type DerivedKeys,
  type WalletSigner,
} from '@ownyou/identity';

export type { Wallet } from '@ownyou/identity';

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  wallet: Wallet | null;
  keys: DerivedKeys | null;
  error: string | null;
}

interface AuthContextValue extends AuthState {
  /** Open Privy login modal */
  connect: () => Promise<void>;
  /** Log out and clear session */
  disconnect: () => Promise<void>;
  /** Sign a message with the embedded wallet */
  signMessage: (message: string) => Promise<string | null>;
  /** Get the WalletSigner for key derivation (advanced usage) */
  signer: WalletSigner | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * AuthProvider - wraps children with Privy wallet auth state.
 *
 * Must be rendered inside a PrivyProvider (see main.tsx).
 * Derives encryption keys when wallet becomes available.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const { login, logout, authenticated, ready } = usePrivy();
  const { wallets } = useWallets();

  const adapter = useMemo(() => new PrivyAdapter(), []);
  const keyDerivation = useMemo(() => new KeyDerivation(), []);

  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    wallet: null,
    keys: null,
    error: null,
  });

  const [currentSigner, setCurrentSigner] = useState<WalletSigner | null>(null);

  // Track which wallet address we've already derived keys for,
  // so we don't re-trigger the EIP-712 signing modal on every navigation.
  const derivedForAddressRef = useRef<string | null>(null);

  // Stable wallet address — avoids re-triggering effect when Privy
  // returns a new wallets array reference on every render.
  const embeddedWalletAddress = useMemo(() => {
    const w = adapter.getEmbeddedWallet(wallets as any[]);
    return w ? (w as any).address as string : null;
  }, [wallets, adapter]);

  // React to Privy auth state changes
  useEffect(() => {
    if (!ready) return;

    if (!authenticated) {
      setState({
        isAuthenticated: false,
        isLoading: false,
        wallet: null,
        keys: null,
        error: null,
      });
      setCurrentSigner(null);
      derivedForAddressRef.current = null;
      return;
    }

    // Find the embedded wallet
    const embeddedWallet = adapter.getEmbeddedWallet(wallets as any[]);
    if (!embeddedWallet || !embeddedWalletAddress) {
      // Wallet not yet loaded — Privy may still be connecting
      setState((prev) => ({ ...prev, isLoading: true }));
      return;
    }

    // Skip if we've already derived keys for this wallet address
    if (derivedForAddressRef.current === embeddedWalletAddress) {
      return;
    }

    const wallet = adapter.toOwnYouWallet(embeddedWallet as any);
    const signer = adapter.createSigner(embeddedWallet as any);
    setCurrentSigner(signer);

    // Derive encryption keys — use cached signature if available to avoid
    // re-showing the Privy signing modal on page reloads.
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    (async () => {
      try {
        let keys: DerivedKeys;

        // Check sessionStorage for a cached master signature
        const SIG_CACHE_KEY = 'ownyou_master_sig';
        const cached = sessionStorage.getItem(SIG_CACHE_KEY);
        let usedCache = false;

        if (cached) {
          try {
            const { addr, sig } = JSON.parse(cached);
            if (addr === embeddedWalletAddress && sig) {
              // Re-derive from cached signature — no Privy modal
              keys = await keyDerivation.deriveAllKeysFromSignature(
                sig as `0x${string}`,
                embeddedWalletAddress
              );
              usedCache = true;
            }
          } catch {
            sessionStorage.removeItem(SIG_CACHE_KEY);
          }
        }

        if (!usedCache) {
          // First sign in this session — triggers Privy's EIP-712 modal
          const sig = await keyDerivation.getMasterSignature(signer);
          sessionStorage.setItem(
            SIG_CACHE_KEY,
            JSON.stringify({ addr: embeddedWalletAddress, sig })
          );
          keys = await keyDerivation.deriveAllKeysFromSignature(
            sig,
            embeddedWalletAddress
          );
        }

        derivedForAddressRef.current = embeddedWalletAddress;
        setState({
          isAuthenticated: true,
          isLoading: false,
          wallet,
          keys: keys!,
          error: null,
        });
        console.log(
          '[AuthContext] Privy auth complete',
          usedCache ? '(cached sig)' : '(fresh sig)',
          'wallet:',
          wallet.address.slice(0, 10) + '...'
        );
      } catch (err) {
        console.error('[AuthContext] Key derivation failed:', err);
        // Still authenticate — keys can be re-derived later
        derivedForAddressRef.current = embeddedWalletAddress;
        setState({
          isAuthenticated: true,
          isLoading: false,
          wallet,
          keys: null,
          error: 'Key derivation failed. Encryption unavailable.',
        });
      }
    })();
  }, [ready, authenticated, embeddedWalletAddress, wallets, adapter, keyDerivation]);

  const connect = useCallback(async () => {
    try {
      login();
    } catch (err) {
      console.error('[AuthContext] Login failed:', err);
      setState((prev) => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Login failed',
      }));
    }
  }, [login]);

  const disconnect = useCallback(async () => {
    try {
      // Privy's logout() calls localStorage.clear() internally,
      // which nukes our OAuth tokens, preferences, etc.
      // Preserve all ownyou_* keys across the logout call.
      const preserved: Record<string, string> = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('ownyou')) {
          preserved[key] = localStorage.getItem(key)!;
        }
      }

      await logout();

      // Restore our keys that Privy wiped
      for (const [key, value] of Object.entries(preserved)) {
        localStorage.setItem(key, value);
      }

      sessionStorage.removeItem('ownyou_master_sig');
      setState({
        isAuthenticated: false,
        isLoading: false,
        wallet: null,
        keys: null,
        error: null,
      });
      setCurrentSigner(null);
    } catch (err) {
      console.error('[AuthContext] Logout failed:', err);
      setState((prev) => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Logout failed',
      }));
    }
  }, [logout]);

  const signMessage = useCallback(
    async (message: string): Promise<string | null> => {
      if (!currentSigner) return null;

      try {
        // Use the EIP-1193 provider to sign a personal message
        const embeddedWallet = adapter.getEmbeddedWallet(wallets as any[]);
        if (!embeddedWallet) return null;

        const provider = await (embeddedWallet as any).getEthereumProvider();
        const signature = await provider.request({
          method: 'personal_sign',
          params: [message, currentSigner.getAddress()],
        });
        return signature as string;
      } catch (err) {
        console.error('[AuthContext] Sign message failed:', err);
        return null;
      }
    },
    [currentSigner, wallets, adapter]
  );

  const value: AuthContextValue = {
    ...state,
    connect,
    disconnect,
    signMessage,
    signer: currentSigner,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
